/**
 * Client-safe auth types. No `server-only` import here — v1 auth is entirely
 * client-side, so these are consumed only by the client session module (`./client`)
 * and its React consumers (spec C-4 / RD-4).
 */

/** Non-anonymous providers a logged-in session can carry. */
export type AuthProvider = 'apple' | 'cognito' | 'facebook' | 'google'

/**
 * Client-safe session context exposed by `useSession()`. Carries no token —
 * the token lives only in the localStorage mirror (`LocalStorageTokenData`).
 */
export interface SessionContext {
    isAuthenticated: boolean
    email?: string
    provider?: AuthProvider
    sessionId?: string
    /** From `userinfo.json` (FR-15), fetched post-hydration — absent until it resolves. */
    firstName?: string
    lastName?: string
}

/**
 * Shape of the cross-app localStorage `userAccessToken` mirror, matching
 * ui-react (`AccountService`) and canopy (`local-storage-bridge.ts`).
 * `provider: 'anonymous'` (canopy anonymous sessions) is treated as logged-out.
 */
export interface LocalStorageTokenData {
    authToken: string
    /**
     * Token expiry as written by the producing app. **ISO-8601 string** in the
     * real cross-app mirror (ui-react/canopy, e.g. `"2026-06-17T21:24:48.000Z"`);
     * a numeric Unix timestamp (seconds or ms) is also accepted on read. Nexus
     * **writes** it as an ISO string (ui-react parses with `moment()`, which misreads a
     * bare numeric-seconds value). Parsed by `readMirror` — never compare it raw.
     */
    authTokenExp: number | string | null
    email: string | null
    identityId: string | null
    provider: string
    sessionId: string
    /** Provider access token (Google id_token / FB token) — ui-react stores this too. */
    accessToken?: string
}

/** localStorage key for the cross-app session mirror. */
export const MIRROR_STORAGE_KEY = 'userAccessToken'
